import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

const guestButtonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-full font-sans font-semibold',
    // Bewusst KEIN whitespace-nowrap: ein Knopf, dessen Beschriftung breiter ist als das
    // Fenster, laeuft sonst ueber beide Raender hinaus statt umzubrechen — auf einem Telefon
    // ist die Beschriftung dann an beiden Enden abgeschnitten. max-w-full haelt ihn zusaetzlich
    // innerhalb seines Containers, auch wenn ein einzelnes langes Wort nicht umbrechen kann.
    'transition-colors max-w-full select-none',
    'disabled:opacity-40 disabled:pointer-events-none',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-accent',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-white hover:bg-accent-deep',
        secondary: 'bg-sand text-fg hover:bg-sand/70',
        outline: 'border border-accent bg-transparent text-accent hover:bg-forest-soft',
      },
      size: {
        // min-h statt mehr Innenabstand: die Knoepfe behalten ihre Proportion, die
        // Trefferflaeche waechst auf das, was ein Daumen sicher trifft.
        sm: 'min-h-11 px-3.5 py-1.5 text-xs',
        md: 'min-h-11 px-5 py-2.5 text-sm',
        lg: 'min-h-13 px-6 py-3.5 text-base',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface GuestButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof guestButtonVariants> {
  asChild?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const GuestButton = forwardRef<HTMLButtonElement, GuestButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      fullWidth,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const classes = cn(guestButtonVariants({ variant, size }), fullWidth && 'w-full', className);

    // Slot merged nur EIN Child — bei asChild wandern Icons in das Consumer-Child.
    if (asChild) {
      return (
        <Comp className={classes} ref={ref} {...props}>
          {children}
        </Comp>
      );
    }

    return (
      <Comp className={classes} ref={ref} disabled={disabled || loading} {...props}>
        {loading ? (
          <Loader2 className="size-4 animate-spin" strokeWidth={2} aria-hidden />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </Comp>
    );
  },
);
GuestButton.displayName = 'GuestButton';

export { guestButtonVariants };
